import React, { useEffect, useRef, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import { itemEffectiveUnitPrice } from './AdminBidDetail';
import NdotBidEstimate from './NdotBidEstimate';

// NDOT Price History (Ryan, Oct 10 2026): the Section 623 items off NDOT's bid tabs — ours from the
// NDOT #### job folders and the ones NDOT publishes — by item, bid date and district (1, 2, 3), with
// the high and low (when, where, who), the average and median, and what we put on our own bids next to it.
// Excel "Unofficial Bid Tab" files are read straight; scanned PDFs are read by the AI (an approved
// exception) and show as a preview to spot-check before saving.
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' };
const on = { background: '#1e3a5f', borderColor: '#1e3a5f', color: '#fff' };
const inp = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '7px 10px', fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const money = n => (n == null || isNaN(n) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);
const dt = s => { if (!s) return '—'; const d = new Date(String(s).slice(0, 10) + 'T12:00:00'); return isNaN(d) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const itemFmt = no => (no && no.length === 7 ? `${no.slice(0, 3)} ${no.slice(3)}` : no || '');
const DCOLOR = { 1: '#b45309', 2: '#1d4ed8', 3: '#15803d' };
const isTitan = name => /titan/i.test(name || '');

function Extreme({ label, x, color }) {
  if (!x) return null;
  return (
    <div style={{ flex: '1 1 220px', background: '#fff', border: '1px solid #e5e7eb', borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '2px 0' }}>{money(x.unit_price)}</div>
      <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.45 }}>
        <b>{x.bidder}</b>{x.rank ? ` (${x.rank === 1 ? 'low bidder' : `bidder #${x.rank}`})` : ''}<br />
        Contract {x.contract_no} · {dt(x.bid_date)}<br />
        District {x.district}{x.county ? ` · ${x.county}` : ''}{x.quantity != null ? ` · qty ${x.quantity}` : ''}
        {x.location && <div style={{ color: '#6b7280', fontSize: 12 }}>{x.location}</div>}
      </div>
    </div>
  );
}

function StatBox({ label, value, sub, color = '#111827' }) {
  return (
    <div style={{ flex: '1 1 120px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>}
    </div>
  );
}

// What we put on our own bids for this item, priced the way the bid page prices it.
function OurBids({ itemNo, stats }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let dead = false; setRows(null);
    (async () => {
      const list = await apiFetch(`/ndot-prices/ours?item=${itemNo}`).catch(() => []);
      const byBid = {};
      for (const r of list) {
        if (!byBid[r.bid_id]) byBid[r.bid_id] = apiFetch(`/bids/${r.bid_id}`).catch(() => null);
      }
      const out = [];
      for (const r of list) {
        const bid = await byBid[r.bid_id];
        const raw = bid && (bid.items || bid.bid_items || []).find(i => i.id === r.id);
        let item = null;
        try { item = raw ? { ...raw, sub_items: raw.sub_items ? (typeof raw.sub_items === 'string' ? JSON.parse(raw.sub_items) : raw.sub_items) : [] } : null; } catch { item = raw ? { ...raw, sub_items: [] } : null; }
        let price = null; try { price = item ? itemEffectiveUnitPrice(item, bid) : null; } catch { price = null; }
        out.push({ ...r, price });
      }
      if (!dead) setRows(out);
    })();
    return () => { dead = true; };
  }, [itemNo]);
  if (rows == null) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Looking through our bids…</div>;
  if (!rows.length) return <div style={{ fontSize: 13, color: '#6b7280' }}>None of our bids in the app carry item {itemFmt(itemNo)} yet.</div>;
  const avg = stats && stats.average;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Our bid</th><th style={th}>Bid date</th><th style={th}>Qty</th><th style={th}>Our unit price</th><th style={th}>vs NDOT average</th></tr></thead>
        <tbody>{rows.map(r => {
          const diff = r.price != null && avg ? (r.price - avg) / avg : null;
          return (
            <tr key={r.id}>
              <td style={td}><a href={`/admin/bids/${r.bid_id}`} style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'none' }}>{r.job_name || 'Bid'}</a>{r.status ? <span style={{ color: '#9ca3af', fontSize: 12 }}> · {r.status}</span> : null}</td>
              <td style={td}>{dt(r.bid_date)}</td>
              <td style={td}>{r.quantity ?? '—'}</td>
              <td style={{ ...td, fontWeight: 800 }}>{money(r.price)}</td>
              <td style={{ ...td, fontWeight: 700, color: diff == null ? '#6b7280' : diff > 0.15 ? '#b91c1c' : diff < -0.15 ? '#b45309' : '#15803d' }}>
                {diff == null ? '—' : `${diff > 0 ? '+' : ''}${Math.round(diff * 100)}%`}
              </td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function ItemView({ itemNo }) {
  const [districts, setDistricts] = useState([1, 2, 3]);
  const [years, setYears] = useState(0);
  const [lowOnly, setLowOnly] = useState(false);
  const [d, setD] = useState(null);
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    setD(null);
    apiFetch(`/ndot-prices/item/${itemNo}?districts=${districts.join(',')}&years=${years}${lowOnly ? '&low=1' : ''}`).then(setD).catch(() => setD({ error: true }));
  }, [itemNo, districts, years, lowOnly]);
  const toggleD = n => setDistricts(list => (list.includes(n) ? (list.length > 1 ? list.filter(x => x !== n) : list) : [...list, n].sort()));
  const maxAvg = d && d.by_year ? Math.max(...d.by_year.map(y => y.average || 0), 1) : 1;
  const rows = d && d.rows ? d.rows.filter(r => !lowOnly || r.is_engineer || r.rank === 1) : [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        {[1, 2, 3].map(n => (
          <button key={n} onClick={() => toggleD(n)} style={{ ...btn, ...(districts.includes(n) ? { background: DCOLOR[n], borderColor: DCOLOR[n], color: '#fff' } : {}) }}>District {n}</button>
        ))}
        <span style={{ width: 10 }} />
        {[[0, 'All years'], [2, 'Last 2 yrs'], [5, 'Last 5 yrs'], [10, 'Last 10 yrs']].map(([v, l]) => (
          <button key={v} onClick={() => setYears(v)} style={{ ...btn, ...(years === v ? on : {}) }}>{l}</button>
        ))}
        <span style={{ width: 10 }} />
        <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} /> Low bidder only
        </label>
      </div>

      {!d ? <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div> : d.error ? <div style={{ color: '#b91c1c', fontSize: 13 }}>Could not load that item.</div> : (<>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 10 }}>
          {itemFmt(d.item_no)} — {d.description || 'item'} <span style={{ color: '#6b7280', fontWeight: 600 }}>({d.unit || 'unit'})</span>
        </div>
        {!d.overall.n ? <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>No bids for this item in the districts and years picked.</div> : (<>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <StatBox label="Average" value={money(d.overall.average)} sub={`${d.overall.n} prices · ${d.overall.contracts} contracts`} />
            <StatBox label="Median" value={money(d.overall.median)} />
            <StatBox label="Engineer’s estimate (avg)" value={money(d.engineer.average)} sub={d.engineer.n ? `${d.engineer.n} contracts` : 'none'} color="#6b7280" />
          </div>
          {d.left_out > 0 && <div style={{ fontSize: 12.5, color: '#6b7280', margin: '-2px 0 10px' }}>{d.left_out} Road and Highway Builders price{d.left_out === 1 ? ' is' : 's are'} listed below but left out of the high, low and average.</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Extreme label="High" x={d.overall.high} color="#b91c1c" />
            <Extreme label="Low" x={d.overall.low} color="#15803d" />
          </div>

          <div className="admin-card" style={{ padding: 14, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>By district</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>District</th><th style={th}>Prices</th><th style={th}>Average</th><th style={th}>Median</th><th style={th}>Low (where)</th><th style={th}>High (where)</th></tr></thead>
                <tbody>{[1, 2, 3].filter(n => districts.includes(n)).map(n => { const s = d.by_district[n] || { n: 0 }; return (
                  <tr key={n}>
                    <td style={{ ...td, fontWeight: 800, color: DCOLOR[n] }}>District {n}</td>
                    <td style={td}>{s.n || '—'}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{s.n ? money(s.average) : '—'}</td>
                    <td style={td}>{s.n ? money(s.median) : '—'}</td>
                    <td style={td}>{s.low ? <>{money(s.low.unit_price)} <span style={{ color: '#6b7280', fontSize: 12 }}>· {s.low.contract_no}, {dt(s.low.bid_date)}</span></> : '—'}</td>
                    <td style={td}>{s.high ? <>{money(s.high.unit_price)} <span style={{ color: '#6b7280', fontSize: 12 }}>· {s.high.contract_no}, {dt(s.high.bid_date)}</span></> : '—'}</td>
                  </tr>
                ); })}</tbody>
              </table>
            </div>
          </div>

          {d.by_year.length > 0 && (
            <div className="admin-card" style={{ padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Average by year</div>
              {d.by_year.map(y => (
                <div key={y.year} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                  <div style={{ width: 42, fontSize: 13, fontWeight: 700, color: '#374151' }}>{y.year}</div>
                  <div style={{ flex: 1, background: '#f3f4f6', borderRadius: 6, height: 18, position: 'relative' }}>
                    <div style={{ width: `${Math.max(2, (y.average / maxAvg) * 100)}%`, background: '#1e3a5f', height: '100%', borderRadius: 6 }} />
                  </div>
                  <div style={{ width: 210, fontSize: 12.5, color: '#374151' }}><b>{money(y.average)}</b> <span style={{ color: '#6b7280' }}>· {money(y.low && y.low.unit_price)}–{money(y.high && y.high.unit_price)} · {y.n}</span></div>
                </div>
              ))}
            </div>
          )}
        </>)}

        <div className="admin-card" style={{ padding: 14, marginBottom: 16, borderTop: '3px solid #e87722' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Ours, next to it</div>
          <OurBids itemNo={d.item_no} stats={d.overall} />
        </div>

        {rows.length > 0 && (
          <div className="admin-card" style={{ padding: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Every price ({rows.length})</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Bid date</th><th style={th}>Contract</th><th style={th}>District</th><th style={th}>Bidder</th><th style={th}>Qty</th><th style={th}>Unit price</th></tr></thead>
                <tbody>{(showAll ? rows : rows.slice(0, 60)).map((r, i) => (
                  <tr key={i} style={{ background: r.is_engineer ? '#f9fafb' : isTitan(r.bidder) ? '#fff7ed' : undefined, opacity: r.left_out ? 0.5 : 1 }} title={r.left_out ? 'Road and Highway Builders — not counted in the high, low or average' : undefined}>
                    <td style={td}>{dt(r.bid_date)}</td>
                    <td style={td}>{r.contract_no}</td>
                    <td style={{ ...td, color: DCOLOR[r.district], fontWeight: 700 }}>{r.district}{r.county ? <span style={{ color: '#6b7280', fontWeight: 400 }}> · {r.county}</span> : ''}</td>
                    <td style={{ ...td, color: r.is_engineer ? '#6b7280' : '#111827', fontStyle: r.is_engineer ? 'italic' : 'normal', fontWeight: isTitan(r.bidder) ? 800 : 400 }}>{r.bidder}{r.rank === 1 ? <span style={{ color: '#15803d', fontWeight: 700, fontSize: 12 }}> · low</span> : ''}{r.left_out ? <span style={{ color: '#6b7280', fontWeight: 700, fontSize: 12 }}> · not counted</span> : ''}</td>
                    <td style={td}>{r.quantity ?? '—'}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{money(r.unit_price)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {rows.length > 60 && !showAll && <button onClick={() => setShowAll(true)} style={{ ...btn, marginTop: 10 }}>Show all {rows.length}</button>}
          </div>
        )}
      </>)}
    </div>
  );
}

// Bring bid tabs in: drop PDFs / Excel files → each is read → check it → Save.
function ImportBox({ onSaved }) {
  const [queue, setQueue] = useState([]); // { key, name, status: 'reading'|'ready'|'saving'|'saved'|'error', preview, error }
  const fileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const upd = (key, patch) => setQueue(q => q.map(x => (x.key === key ? { ...x, ...patch } : x)));

  const addFiles = async files => {
    const list = Array.from(files || []).filter(f => /\.(pdf|xlsx?)$/i.test(f.name));
    const items = list.map(f => ({ key: `${f.name}-${Math.random()}`, name: f.name, file: f, status: 'reading' }));
    setQueue(q => [...items, ...q]);
    // One at a time — the AI reads a scanned tab for a minute or two.
    for (const it of items) {
      try {
        const fd = new FormData(); fd.append('file', it.file, it.name);
        const r = await fetch(`${API_BASE}/ndot-prices/read`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Could not read it (${r.status})`);
        upd(it.key, { status: 'ready', preview: j });
      } catch (e) { upd(it.key, { status: 'error', error: e.message }); }
    }
  };
  const save = async it => {
    upd(it.key, { status: 'saving' });
    try {
      const r = await apiFetch('/ndot-prices/contracts', { method: 'POST', body: JSON.stringify(it.preview) });
      upd(it.key, { status: 'saved', savedNote: `${r.items} items saved${r.replaced ? ' (replaced the one already in)' : ''}` });
      onSaved && onSaved();
    } catch (e) { upd(it.key, { status: 'ready', error: e.error || 'Could not save' }); }
  };
  const setP = (key, patch) => setQueue(q => q.map(x => (x.key === key ? { ...x, preview: { ...x.preview, ...patch }, error: null } : x)));

  return (
    <div>
      <div onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current && fileRef.current.click()}
        style={{ border: `2px dashed ${drag ? '#1e3a5f' : '#d1d5db'}`, background: drag ? '#eff6ff' : '#fafafa', borderRadius: 12, padding: 22, textAlign: 'center', cursor: 'pointer', marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>Drop NDOT bid tabs here</div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 4 }}>Excel “Unofficial Bid Tab” files are read straight off. Scanned “Certified Bid Tab” PDFs are read by the AI — check the numbers before you save. Only the Section 623 items are kept.</div>
        <input ref={fileRef} type="file" multiple accept=".pdf,.xlsx,.xls" style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
      </div>
      {queue.map(it => {
        const p = it.preview;
        return (
          <div key={it.key} className="admin-card" style={{ padding: 14, marginBottom: 10, borderLeft: `4px solid ${it.status === 'saved' ? '#15803d' : it.status === 'error' ? '#b91c1c' : '#1e3a5f'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 800, fontSize: 13.5 }}>{it.name}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: it.status === 'saved' ? '#15803d' : it.status === 'error' ? '#b91c1c' : '#6b7280' }}>
                {it.status === 'reading' ? (/\.pdf$/i.test(it.name) ? 'The AI is reading it… (a minute or two)' : 'Reading…') : it.status === 'saving' ? 'Saving…' : it.status === 'saved' ? `✓ ${it.savedNote}` : it.status === 'error' ? it.error : ''}
              </div>
            </div>
            {p && it.status !== 'saved' && (<>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10, alignItems: 'flex-end' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>CONTRACT #<br /><input value={p.contract_no || ''} onChange={e => setP(it.key, { contract_no: e.target.value })} style={{ ...inp, width: 100 }} /></label>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>BID DATE<br /><input type="date" value={p.bid_date || ''} onChange={e => setP(it.key, { bid_date: e.target.value })} style={inp} /></label>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>DISTRICT<br />
                  <select value={p.district || ''} onChange={e => setP(it.key, { district: Number(e.target.value) || null })} style={inp}>
                    <option value="">Pick…</option><option value="1">District 1</option><option value="2">District 2</option><option value="3">District 3</option>
                  </select></label>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280' }}>COUNTY<br /><input value={p.county || ''} onChange={e => setP(it.key, { county: e.target.value })} style={{ ...inp, width: 130 }} /></label>
                <div style={{ fontSize: 12.5, color: '#374151', paddingBottom: 8 }}>{(p.bidders || []).length} bidders · <b>{(p.items || []).length} Section 623 items</b>{p.read_by === 'ai' ? ' · read by AI' : ''}</div>
                <button onClick={() => save(it)} disabled={it.status === 'saving' || !(p.items || []).length} style={{ ...btn, ...on, marginLeft: 'auto' }}>{p.exists ? 'Save (replace the one in)' : 'Save'}</button>
                <button onClick={() => setQueue(q => q.filter(x => x.key !== it.key))} style={btn}>Skip</button>
              </div>
              {p.location && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{p.location}</div>}
              {(p.warnings || []).length > 0 && <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 6 }}>{p.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}</div>}
              {it.error && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 6 }}>{it.error}</div>}
              {(p.items || []).length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12.5, fontWeight: 700, color: '#1d4ed8', cursor: 'pointer' }}>Check the prices</summary>
                  <div style={{ overflowX: 'auto', marginTop: 6 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr><th style={th}>Item</th><th style={th}>Description</th><th style={th}>Qty</th><th style={th}>Engineer</th>{(p.bidders || []).map((b, k) => <th key={k} style={th} title={b}>{k + 1}. {String(b).slice(0, 18)}</th>)}</tr></thead>
                      <tbody>{p.items.map((x, i) => (
                        <tr key={i}>
                          <td style={td}>{itemFmt(x.item_no)}</td><td style={{ ...td, minWidth: 180 }}>{x.description}</td><td style={td}>{x.quantity} {x.unit}</td>
                          <td style={{ ...td, color: '#6b7280' }}>{money(x.engineer_unit_price)}</td>
                          {(p.bidders || []).map((_, k) => <td key={k} style={td}>{money((x.unit_prices || [])[k])}</td>)}
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </details>
              )}
            </>)}
          </div>
        );
      })}
    </div>
  );
}

function ContractsList({ refresh, onChanged }) {
  const [list, setList] = useState(null);
  useEffect(() => { apiFetch('/ndot-prices/contracts').then(setList).catch(() => setList([])); }, [refresh]);
  const del = async c => {
    if (!window.confirm(`Take contract ${c.contract_no} out of the price history?`)) return;
    await apiFetch(`/ndot-prices/contracts/${c.id}`, { method: 'DELETE' }).catch(e => alert(e.error || 'Could not remove it'));
    onChanged && onChanged();
  };
  if (!list) return <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>;
  if (!list.length) return <div style={{ color: '#6b7280', fontSize: 13 }}>No bid tabs in yet — drop some in above.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Contract</th><th style={th}>Bid date</th><th style={th}>District</th><th style={th}>Where</th><th style={th}>Bidders</th><th style={th}>623 items</th><th style={th}>Read from</th><th style={th}></th></tr></thead>
        <tbody>{list.map(c => (
          <tr key={c.id}>
            <td style={{ ...td, fontWeight: 800 }}>{c.contract_no}</td>
            <td style={td}>{dt(c.bid_date)}</td>
            <td style={{ ...td, fontWeight: 700, color: DCOLOR[c.district] }}>{c.district}</td>
            <td style={{ ...td, maxWidth: 280 }}>{c.county || ''}{c.location ? <div style={{ fontSize: 12, color: '#6b7280' }}>{c.location}</div> : null}</td>
            <td style={td} title={(c.bidders || []).join('\n')}>{(c.bidders || []).length}{(c.bidders || []).some(isTitan) ? <span style={{ color: '#c2410c', fontWeight: 700 }}> · Titan bid</span> : ''}</td>
            <td style={td}>{c.item_count}</td>
            <td style={{ ...td, fontSize: 12, color: '#6b7280' }}>{c.read_by === 'ai' ? 'PDF (AI)' : 'Excel'}</td>
            <td style={td}><button onClick={() => del(c)} style={{ ...btn, color: '#b91c1c' }}>Remove</button></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function NdotPricesPanel() {
  const [view, setViewRaw] = useState(() => { try { return localStorage.getItem('titan_ndot_view') || 'look'; } catch { return 'look'; } });
  const setView = v => { setViewRaw(v); try { localStorage.setItem('titan_ndot_view', v); } catch { /* per-viewer */ } };
  const [q, setQ] = useState('');
  const [items, setItems] = useState(null);
  const [itemNo, setItemNo] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => apiFetch(`/ndot-prices/items?q=${encodeURIComponent(q)}`).then(setItems).catch(() => setItems([])), 200);
    return () => clearTimeout(t);
  }, [q, refresh]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setView('look')} style={{ ...btn, ...(view === 'look' ? on : {}) }}>Look up an item</button>
        <button onClick={() => setView('estimate')} style={{ ...btn, ...(view === 'estimate' ? on : {}) }}>Price a bid</button>
        <button onClick={() => setView('import')} style={{ ...btn, ...(view === 'import' ? on : {}) }}>Bring bid tabs in</button>
      </div>

      {view === 'estimate' ? <NdotBidEstimate /> : view === 'import' ? (<>
        <ImportBox onSaved={() => setRefresh(n => n + 1)} />
        <h3 style={{ fontSize: 13, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 8px' }}>In the history</h3>
        <ContractsList refresh={refresh} onChanged={() => setRefresh(n => n + 1)} />
      </>) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: 18, alignItems: 'start' }} className="ndot-grid">
          <style>{'@media (max-width: 760px) { .ndot-grid { grid-template-columns: 1fr !important; } }'}</style>
          <div className="admin-card" style={{ padding: 12 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Item # or words — 623 0267, luminaire…" style={{ ...inp, width: '100%', marginBottom: 8 }} />
            {items == null ? <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div> : !items.length ? (
              <div style={{ color: '#6b7280', fontSize: 13 }}>{q ? 'Nothing matches.' : 'No bid tabs in yet — use “Bring bid tabs in”.'}</div>
            ) : (
              <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                {items.map(i => (
                  <button key={i.item_no} onClick={() => setItemNo(i.item_no)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 8, padding: '7px 9px', marginBottom: 2, cursor: 'pointer', fontFamily: 'inherit', background: itemNo === i.item_no ? '#eff6ff' : 'transparent' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{itemFmt(i.item_no)} <span style={{ fontWeight: 500, color: '#6b7280', fontSize: 12 }}>· {i.contracts} contract{i.contracts === 1 ? '' : 's'}</span></div>
                    <div style={{ fontSize: 12.5, color: '#374151' }}>{i.description}{i.unit ? <span style={{ color: '#9ca3af' }}> ({i.unit})</span> : null}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {itemNo ? <ItemView key={itemNo} itemNo={itemNo} /> : (
              <div className="admin-card" style={{ padding: 20, color: '#6b7280', fontSize: 13.5 }}>
                Pick an item on the left to see its high and low (when, where and who), the average and median, by district and by year — and what we bid it at.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
